import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, MoneyInput, SummaryRow, PayButton, PleaseWait, Dropdown, TextField,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface PaymentMethod { id: string; name: string; }
interface Game { id: string; name: string; credentialRequestsEnabled?: boolean; }
interface WithdrawLimits { min: number; max: number; }
interface WalletSummary { balance: number; }

type WithdrawMode = 'choice' | 'redemption' | 'gameBank' | 'wallet';

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function Withdraw() {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { alert } = useAlert();

  const initialMode = (() => {
    const m = new URLSearchParams(location.search).get('mode');
    return m === 'wallet' || m === 'redemption' || m === 'gameBank' ? (m as WithdrawMode) : 'choice';
  })();

  const [mode, setMode] = useState<WithdrawMode>(initialMode);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [limits, setLimits] = useState<WithdrawLimits>({ min: 40, max: 2000 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [methodsRes, gamesRes, limitsRes, walletRes] = await Promise.allSettled([
          api.get<PaymentMethod[]>('/payment-methods'),
          api.get<Game[]>('/games'),
          api.get<WithdrawLimits>('/orders/withdraw/my-limits'),
          api.get<WalletSummary>('/wallet/me'),
        ]);
        if (methodsRes.status === 'fulfilled') {
          setPaymentMethods(methodsRes.value.data);
          if (methodsRes.value.data.length > 0) setSelectedMethod(methodsRes.value.data[0]);
        }
        if (gamesRes.status === 'fulfilled') {
          setGames(gamesRes.value.data.filter(allowsCredentialRequests));
        }
        if (limitsRes.status === 'fulfilled') setLimits(limitsRes.value.data);
        if (walletRes.status === 'fulfilled') setWalletBalance(Number(walletRes.value.data.balance || 0));
        if (methodsRes.status === 'rejected' || gamesRes.status === 'rejected') {
          alert('Load Warning', 'Some withdrawal options could not be loaded. Please try again in a moment.');
        }
      } catch {
        alert('Error', 'Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [alert]);

  const resetForm = (nextMode: WithdrawMode) => {
    setAmount('');
    setTag('');
    if (nextMode === 'wallet') setSelectedGame(null);
    setMode(nextMode);
    setShowReviewModal(false);
  };

  const getValidatedAmount = () => {
    const parsed = Number(amount);
    if (!amount || Number.isNaN(parsed) || parsed <= 0) {
      alert('Invalid Amount', 'Please enter a valid amount.');
      return null;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      alert('Invalid Amount', 'Amounts can only use up to 2 decimal places.');
      return null;
    }
    const parsedCents = Math.round(parsed * 100);
    if (mode === 'redemption' || mode === 'gameBank') {
      if (!selectedGame) { alert('Missing Info', 'Please select the game you want to withdraw from.'); return null; }
      if (parsedCents < Math.round(limits.min * 100)) { alert('Amount Too Low', `Minimum game withdrawal is $${limits.min}.`); return null; }
      if (parsedCents > Math.round(limits.max * 100)) { alert('Amount Too High', `Maximum game withdrawal is $${limits.max}.`); return null; }
    }
    if (mode === 'gameBank' || mode === 'wallet') {
      if (!selectedMethod) { alert('Missing Info', 'Please select a receiving method.'); return null; }
      if (!tag.trim()) { alert('Missing Info', 'Please enter your payout account.'); return null; }
    }
    if (mode === 'wallet' && parsedCents > Math.round(walletBalance * 100)) {
      alert('Insufficient Balance', `Your wallet has $${walletBalance.toFixed(2)} available.`);
      return null;
    }
    return parsed;
  };

  const handleReview = () => {
    if (getValidatedAmount() == null) return;
    setShowReviewModal(true);
  };

  const handleSubmit = async () => {
    const parsedAmount = getValidatedAmount();
    if (!parsedAmount) return;
    setShowReviewModal(false);
    setSubmitting(true);
    try {
      if (mode === 'redemption') {
        if (!selectedGame) return;
        await api.post('/orders/redemption/request', { gameId: selectedGame.id, amount: parsedAmount });
      } else if (mode === 'gameBank') {
        if (!selectedGame || !selectedMethod) return;
        await api.post('/orders/withdraw', {
          gameId: selectedGame.id,
          paymentMethodId: selectedMethod.id,
          payoutTag: tag.trim(),
          amount: parsedAmount,
        });
      } else {
        if (!selectedMethod) return;
        await api.post('/orders/withdraw/wallet', {
          paymentMethodId: selectedMethod.id,
          payoutTag: tag.trim(),
          amount: parsedAmount,
        });
        setWalletBalance(b => Math.max(0, Math.round(b * 100 - parsedAmount * 100) / 100));
      }
      setAmount('');
      setTag('');
      setShowPleaseWait(true);
      setTimeout(() => {
        setShowPleaseWait(false);
        navigate(mode === 'redemption' ? '/profile' : '/profile?openHistory=1');
      }, 2200);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to submit. Please try again.';
      if (mode === 'wallet' && /below minimum|exceeds maximum/i.test(message)) {
        alert('Withdrawal Mode Error', 'Wallet-to-bank withdrawals do not use game withdrawal limits. Please go back, choose Withdraw Wallet to Bank, and try again.');
      } else {
        alert('Error', message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isRedemption = mode === 'redemption';
  const isGameBank = mode === 'gameBank';
  const isGameSource = isRedemption || isGameBank;
  const reviewTitle = isRedemption ? 'Review Redemption' : 'Review Withdrawal';
  const submitLabel = isRedemption ? 'Submit Redemption' : 'Submit Withdrawal';
  const requestType = isRedemption ? 'Game to Wallet' : isGameBank ? 'Game to Bank' : 'Wallet to Bank';

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="arrow-circle-down"
        title="Withdraw"
        right={<Icon name="shield-alt" size={16} color={colors.accentDark} />}
      />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={4} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          {mode === 'choice' ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 14,
                backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                marginBottom: 14,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  backgroundColor: colors.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="wallet" size={16} color={colors.accentText} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSub }}>Wallet Balance</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: colors.text, marginTop: 2 }}>${walletBalance.toFixed(2)}</div>
                </div>
              </div>

              <PathCard
                icon="gamepad"
                title="Redeem Game Winnings to Wallet"
                body="Move game winnings into your app wallet. Game withdrawal limits apply."
                onClick={() => resetForm('redemption')}
              />
              <PathCard
                icon="university"
                title="Withdraw Game Winnings to Bank"
                body="Cash out game winnings to a payment method. Game withdrawal limits apply."
                onClick={() => resetForm('gameBank')}
              />
              <PathCard
                icon="wallet"
                title="Withdraw Wallet to Bank"
                body="Cash out your available wallet balance. No 3x or max withdrawal limit applies."
                onClick={() => resetForm('wallet')}
              />
            </>
          ) : (
            <>
              <button
                onClick={() => resetForm('choice')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: colors.textSub, fontSize: 13, fontWeight: 800,
                  marginBottom: 4, minHeight: 44,
                }}
              >
                <Icon name="arrow-left" size={13} color={colors.textSub} />
                Change withdrawal type
              </button>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 14,
                backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 14,
                  backgroundColor: isGameSource ? colors.infoSoft : colors.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={isGameSource ? 'gamepad' : 'wallet'} size={13} color={isGameSource ? colors.infoText : colors.accentText} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSub, letterSpacing: 0.3 }}>
                    {isRedemption ? 'Game-to-Wallet Redemption' : isGameBank ? 'Game-to-Bank Withdrawal' : 'Wallet-to-Bank Withdrawal'}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: colors.text, marginTop: 2 }}>
                    {isGameSource ? `$${limits.min} - $${limits.max}` : `Available wallet: $${walletBalance.toFixed(2)}`}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                    {isGameSource ? 'Game withdrawal limits apply.' : 'No 3x or maximum withdrawal limit.'}
                  </div>
                </div>
              </div>

              {isGameSource ? (
                <>
                  <SectionLabel>1. Withdraw From Game</SectionLabel>
                  <Dropdown icon="gamepad" text={selectedGame?.name || ''} placeholder="Select a game" onClick={() => setShowGamePicker(true)} />

                  {isGameBank ? (
                    <>
                      <SectionLabel>2. Receiving Method</SectionLabel>
                      <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />

                      <SectionLabel>3. {selectedMethod?.name || 'Receiving'} Account</SectionLabel>
                      <TextField
                        icon="tag"
                        value={tag}
                        onChange={setTag}
                        placeholder={`Enter your ${selectedMethod?.name || 'payout'} account`}
                        autoCapitalize="none"
                      />

                      <SectionLabel>4. Amount (USD)</SectionLabel>
                    </>
                  ) : (
                    <SectionLabel>2. Amount (USD)</SectionLabel>
                  )}
                  <MoneyInput value={amount} onChange={setAmount} placeholder={`Min $${limits.min}`} />
                </>
              ) : (
                <>
                  <SectionLabel>1. Receiving Method</SectionLabel>
                  <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />

                  <SectionLabel>2. {selectedMethod?.name || 'Receiving'} Account</SectionLabel>
                  <TextField
                    icon="tag"
                    value={tag}
                    onChange={setTag}
                    placeholder={`Enter your ${selectedMethod?.name || 'payout'} account`}
                    autoCapitalize="none"
                  />

                  <SectionLabel>3. Amount (USD)</SectionLabel>
                  <MoneyInput value={amount} onChange={setAmount} placeholder={`Max $${walletBalance.toFixed(2)}`} />

                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    backgroundColor: colors.warningSoft, border: `1px solid ${colors.warning}`,
                    borderRadius: 12, padding: 12, marginTop: 18,
                  }}>
                    <Icon name="exclamation-triangle" size={13} color={colors.warningText} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, fontWeight: 600, color: colors.warningText }}>
                      Your wallet balance is debited when you submit. If the request is rejected or cancelled while pending, it is refunded.
                    </div>
                  </div>
                </>
              )}

              <PayButton label={reviewTitle} onClick={handleReview} loading={submitting} />
            </>
          )}
        </div>
      )}

      <Modal open={showReviewModal} onClose={() => setShowReviewModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{reviewTitle}</div>
        <div style={{
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 14, padding: 14, marginBottom: 16,
        }}>
          <SummaryRow label="Type" value={requestType} />
          {isGameSource && <SummaryRow label="Game" value={selectedGame?.name || ''} />}
          {(isGameBank || mode === 'wallet') && (
            <>
              <SummaryRow label="Method" value={selectedMethod?.name || ''} />
              <SummaryRow label="Account" value={tag.trim()} />
            </>
          )}
          <SummaryRow label="Amount" value={`$${Number(amount || 0).toFixed(2)}`} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Cancel" variant="outline" onClick={() => setShowReviewModal(false)} />
          <Button label={submitLabel} loading={submitting} onClick={handleSubmit} />
        </div>
      </Modal>

      <Modal open={showGamePicker} onClose={() => setShowGamePicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Select Game</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {games.map(g => (
            <button
              key={g.id}
              onClick={() => { setSelectedGame(g); setShowGamePicker(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 0', borderBottom: `1px solid ${colors.divider}`,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                minHeight: 50,
              }}
            >
              <Icon name="gamepad" size={14} color={selectedGame?.id === g.id ? '#14b8a6' : colors.textSub} />
              <div style={{
                flex: 1, fontSize: 15,
                color: selectedGame?.id === g.id ? '#14b8a6' : colors.text,
                fontWeight: selectedGame?.id === g.id ? 700 : 400,
              }}>
                {g.name}
              </div>
              {selectedGame?.id === g.id && <Icon name="check" size={13} color="#14b8a6" />}
            </button>
          ))}
        </div>
        <Button label="Cancel" variant="outline" onClick={() => setShowGamePicker(false)} style={{ marginTop: 12 }} />
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title="Request Submitted"
        message={isRedemption ? 'Your redemption request has been submitted.' : 'Your withdrawal request has been submitted.'}
      />
    </div>
  );
}

function MethodGrid({
  methods, selected, onSelect,
}: {
  methods: PaymentMethod[];
  selected: PaymentMethod | null;
  onSelect: (m: PaymentMethod) => void;
}) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {methods.map(m => (
        <Chip
          key={m.id}
          active={selected?.id === m.id}
          onClick={() => onSelect(m)}
          icon={<Icon name="wallet" size={13} color={selected?.id === m.id ? '#020617' : colors.textSub} />}
          label={m.name}
        />
      ))}
    </div>
  );
}

function PathCard({ icon, title, body, onClick }: { icon: string; title: string; body: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14, padding: 14, marginBottom: 12, minHeight: 112,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={18} color={colors.accentText} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: colors.text }}>{title}</div>
        <div style={{ fontSize: 12, lineHeight: 1.4, color: colors.textSub, marginTop: 3 }}>{body}</div>
      </div>
      <div style={{
        backgroundColor: colors.accent, padding: '8px 12px', borderRadius: 999,
        color: '#020617', fontSize: 12, fontWeight: 900, minHeight: 36,
        display: 'flex', alignItems: 'center', flexShrink: 0,
      }}>
        Choose
      </div>
    </button>
  );
}
